import React, { useState, useEffect } from 'react';
import { 
  Search,
  Plus,
  ChevronRight,
  Pencil,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Users,
  Briefcase,
  Calendar,
  Activity,
  ChevronDown
} from 'lucide-react';
import { Store, ProductionStatus, visionService } from '../../services/database/vision';
import { StoreForm } from './StoreForm';
import { toast } from 'sonner';
import { TeamManagement } from './TeamManagement';

interface ProductionListProps {
  isBlurred: boolean;
}

// Status Badge Component
const StatusBadge = ({ status }: { status: ProductionStatus }) => {
  const styles = {
    done: 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/20',
    in_progress: 'bg-[#00B7E2]/20 text-[#00B7E2] border border-[#00B7E2]/20',
    not_started: 'bg-[rgb(44,44,46)]/60 text-[rgb(152,152,157)] border border-[rgb(152,152,157)]/20'
  };

  const labels = {
    done: 'Done',
    in_progress: 'In Progress',
    not_started: 'Not Started'
  };

  return (
    <span className={`px-3 py-1 rounded-2xl text-sm backdrop-blur-md ${styles[status]}`}>
      {labels[status]}
    </span>
  );
};

// Plan Badge Component
const PlanBadge = ({ plan }: { plan: Store['plan'] }) => {
  const styles = {
    branding: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/20',
    premium: 'bg-[#00B7E2]/20 text-[#00B7E2] border border-[#00B7E2]/20',
    start: 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/20',
    pontual: 'bg-red-500/20 text-red-500 border border-red-500/20'
  };

  return (
    <span className={`px-3 py-1 rounded-2xl text-sm backdrop-blur-md ${styles[plan]}`}>
      {plan.charAt(0).toUpperCase() + plan.slice(1)}
    </span>
  );
};

// Filters Component
const FilterButton = ({ 
  icon: Icon, 
  label, 
  options, 
  value, 
  onChange 
}: { 
  icon: LucideIcon;
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`h-12 px-4 rounded-2xl flex items-center gap-2 text-sm font-medium transition-all duration-200 ${
          value ? 'bg-[rgb(28,28,30)] text-white border border-white/10' : 'bg-[rgb(28,28,30)] text-[rgb(152,152,157)] border border-white/10'
        } hover:bg-[rgb(44,44,46)]`}
      >
        <Icon size={20} />
        <span>{options.find(opt => opt.value === value)?.label || label}</span>
        <ChevronDown size={16} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      
      {open && (
        <div className="absolute top-full left-0 mt-2 w-56 bg-[rgb(28,28,30)] rounded-2xl border border-white/10 shadow-lg z-10">
          <div className="p-2">
            {options.map(option => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`w-full px-4 py-2 rounded-xl text-left flex items-center gap-2 text-sm transition-all duration-200 ${
                  value === option.value
                    ? 'bg-[rgb(44,44,46)] text-white'
                    : 'text-[rgb(152,152,157)] hover:bg-[rgb(44,44,46)] hover:text-white'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const ProductionList: React.FC<ProductionListProps> = ({
  isBlurred
}) => {
  const [view, setView] = useState<'production' | 'completed'>('production');
  const [searchTerm, setSearchTerm] = useState('');
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const [filters, setFilters] = useState({
    plan: '',
    status: '',
    date: '',
    member: ''
  });
  const [members, setMembers] = useState([]);

  const loadStores = async () => {
    try {
      const data = await visionService.getStores();
      if (view === 'completed') {
        setStores(data.filter(store => store.completed_at));
      } else {
        setStores(data.filter(store => !store.completed_at));
      }
    } catch (error) {
      console.error('Erro ao carregar lojas:', error);
      toast.error('Erro ao carregar lojas');
    }
  };

  const loadMembers = async () => {
    try {
      const data = await visionService.getTeamMembers();
      setMembers(data);
    } catch (error) {
      console.error('Erro ao carregar membros:', error);
      toast.error('Erro ao carregar membros');
    }
  };

  useEffect(() => {
    loadStores();
    loadMembers();
  }, [view]);

  const handleStatusUpdate = async (store: Store, field: keyof Store['status'], newStatus: ProductionStatus) => {
    try {
      const updatedStatus = { ...store.status, [field]: newStatus };
      
      // Verifica se todos os status estão como 'done'
      const allDone = Object.values(updatedStatus).every(status => status === 'done');
      
      // Atualiza o status e marca como concluída se necessário
      const updates: Partial<Store> = {
        status: updatedStatus,
        completed_at: allDone ? new Date().toISOString() : null
      };
      
      // Extrair os IDs dos membros atuais da loja
      const currentMemberIds = store.team_members.map(member => member.id);
      await visionService.updateStore(store.id, updates, currentMemberIds);
      loadStores();
      toast.success('Status atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const filteredStores = stores.filter(store => {
    const matchesSearch = store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      store.responsibles.some(r => r.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesPlan = !filters.plan || store.plan === filters.plan;
    
    const matchesStatus = !filters.status || Object.values(store.status).some(s => s === filters.status);
    
    let matchesDate = true;
    if (filters.date === 'warning') {
      const daysLeft = Math.ceil((new Date(store.deadline).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
      matchesDate = daysLeft <= 3 && daysLeft > 0;
    } else if (filters.date === 'overdue') {
      matchesDate = new Date() > new Date(store.deadline);
    }
    
    const matchesMember = !filters.member || store.team_members.some(member => member.id === filters.member);
    
    return matchesSearch && matchesPlan && matchesStatus && matchesDate && matchesMember;
  });

  // Group stores by plan
  const groupedStores = filteredStores.reduce((acc, store) => {
    if (!acc[store.plan]) {
      acc[store.plan] = [];
    }
    acc[store.plan].push(store);
    return acc;
  }, {} as Record<Store['plan'], Store[]>);

  // Manter a ordem original das lojas dentro de cada grupo
  Object.keys(groupedStores).forEach(plan => {
    groupedStores[plan].sort((a, b) => {
      // Ordenar por data de criação para manter a ordem original
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
  });

  // Order of plans
  const planOrder: Store['plan'][] = ['branding', 'premium', 'start', 'pontual'];

  const getDeadlineStatus = (deadline: string) => {
    const daysLeft = Math.ceil((new Date(deadline).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
    if (daysLeft <= 0) return { color: 'text-red-500', icon: AlertTriangle };
    if (daysLeft <= 3) return { color: 'text-yellow-500', icon: Clock };
    return { color: 'text-emerald-500', icon: CheckCircle2 };
  };

  const renderStoreName = (name: string) => {
    if (isBlurred) {
      return "••••••";
    }
    return name;
  };

  return (
    <div className="p-6">
      {/* Background com gradiente */}
      <div className="fixed left-0 right-0 top-0 h-[500px] bg-gradient-to-b from-[#00B7E2]/20 via-[#00B7E2]/5 to-transparent pointer-events-none" />

      {/* Conteúdo */}
      <div className="relative space-y-6">
        {/* Tabs */}
        <div className="flex gap-8 border-b border-white/10">
          <button
            onClick={() => setView('production')}
            className={`pb-4 text-[17px] font-medium transition-all duration-200 ${
              view === 'production'
                ? 'border-b-2 border-[#00B7E2] text-white'
                : 'text-[rgb(152,152,157)] hover:text-white'
            }`}
          >
            Em Produção
          </button>
          <button
            onClick={() => setView('completed')}
            className={`pb-4 text-[17px] font-medium transition-all duration-200 ${
              view === 'completed'
                ? 'border-b-2 border-[#00B7E2] text-white'
                : 'text-[rgb(152,152,157)] hover:text-white'
            }`}
          >
            Concluídas
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[rgb(152,152,157)]" size={20} />
            <input
              type="text"
              placeholder="Buscar por nome da loja ou responsável..."
              className="w-full h-12 bg-[rgb(28,28,30)]/60 backdrop-blur-md rounded-2xl pl-12 pr-4 text-white placeholder-[rgb(152,152,157)] border border-white/10 focus:outline-none focus:border-[#00B7E2] transition-all duration-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <FilterButton
            icon={Briefcase}
            label="Filtrar por Plano"
            value={filters.plan}
            onChange={value => setFilters(prev => ({ ...prev, plan: value }))}
            options={[
              { value: '', label: 'Planos' },
              { value: 'branding', label: '🟡 Branding' },
              { value: 'premium', label: '🔵 Premium' },
              { value: 'start', label: '🟢 Start' },
              { value: 'pontual', label: '🔴 Pontual' }
            ]}
          />

          <FilterButton
            icon={Calendar}
            label="Filtrar por Data"
            value={filters.date}
            onChange={value => setFilters(prev => ({ ...prev, date: value }))}
            options={[
              { value: '', label: 'Datas' },
              { value: 'warning', label: '⚠️ Próximo ao Prazo' },
              { value: 'overdue', label: '⏰ Atrasadas' }
            ]}
          />

          <FilterButton
            icon={Activity}
            label="Filtrar por Status"
            value={filters.status}
            onChange={value => setFilters(prev => ({ ...prev, status: value }))}
            options={[
              { value: '', label: 'Status' },
              { value: 'done', label: '✅ Concluído' },
              { value: 'in_progress', label: '🔄 Em Andamento' },
              { value: 'not_started', label: '⭕ Não Iniciado' }
            ]}
          />

          <FilterButton
            icon={Users}
            label="Filtrar por Membro"
            value={filters.member}
            onChange={value => setFilters(prev => ({ ...prev, member: value }))}
            options={[
              { value: '', label: 'Membros' },
              ...members.map(member => ({
                value: member.id,
                label: `👤 ${member.name}`
              }))
            ]}
          />

          <button
            onClick={() => setShowTeamManagement(true)}
            className="h-12 px-4 bg-[rgb(28,28,30)] rounded-2xl text-white border border-white/10 hover:bg-[rgb(44,44,46)] transition-all duration-200 flex items-center gap-2 text-sm font-medium"
          >
            <Users size={20} />
            Equipe
          </button>

          <button
            onClick={() => setShowForm(true)}
            className="h-12 px-4 bg-white hover:bg-white/90 text-black rounded-2xl flex items-center gap-2 text-sm font-medium transition-all duration-200"
          >
            <Plus size={20} />
            Nova Loja
          </button>
        </div>

        {/* Grouped Stores */}
        <div className="space-y-8">
          {planOrder.map(plan => {
            const stores = groupedStores[plan] || [];
            if (!stores.length) return null;

            return (
              <div key={plan} className="space-y-4">
                <div className="flex items-center gap-4">
                  <PlanBadge plan={plan} />
                  <h3 className="text-[20px] font-semibold text-white">
                    {stores.length} {stores.length === 1 ? 'loja' : 'lojas'}
                  </h3>
                </div>

                <div className="bg-[rgb(28,28,30)]/60 backdrop-blur-md rounded-2xl border border-white/10 overflow-hidden transition-all duration-200">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-6 text-[rgb(152,152,157)] font-medium">Loja</th>
                        <th className="text-left p-6 text-[rgb(152,152,157)] font-medium">ID Visual</th>
                        <th className="text-left p-6 text-[rgb(152,152,157)] font-medium">Banners</th>
                        <th className="text-left p-6 text-[rgb(152,152,157)] font-medium">SM</th>
                        <th className="text-left p-6 text-[rgb(152,152,157)] font-medium">Motion</th>
                        <th className="text-left p-6 text-[rgb(152,152,157)] font-medium">Responsáveis</th>
                        <th className="text-left p-6 text-[rgb(152,152,157)] font-medium">Prazo</th>
                        <th className="text-right p-6 text-[rgb(152,152,157)] font-medium">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stores.map(store => {
                        const deadlineStatus = getDeadlineStatus(store.deadline);
                        const DeadlineIcon = deadlineStatus.icon;
                        
                        return (
                          <tr key={store.id} className="border-b border-white/10 hover:bg-[rgb(44,44,46)]/60 transition-all duration-200">
                            <td className="p-6">
                              <div className={`text-[17px] font-medium text-white ${isBlurred ? 'blur-sm' : ''}`}>
                                {renderStoreName(store.name)}
                              </div>
                            </td>
                            <td className="p-6">
                              <button 
                                onClick={() => handleStatusUpdate(store, 'idVisual', 
                                  store.status.idVisual === 'not_started' ? 'in_progress' :
                                  store.status.idVisual === 'in_progress' ? 'done' : 'not_started'
                                )}
                                className="transition-transform hover:scale-105 duration-200"
                              >
                                <StatusBadge status={store.status.idVisual} />
                              </button>
                            </td>
                            <td className="p-6">
                              <button 
                                onClick={() => handleStatusUpdate(store, 'banners',
                                  store.status.banners === 'not_started' ? 'in_progress' :
                                  store.status.banners === 'in_progress' ? 'done' : 'not_started'
                                )}
                                className="transition-transform hover:scale-105 duration-200"
                              >
                                <StatusBadge status={store.status.banners} />
                              </button>
                            </td>
                            <td className="p-6">
                              <button 
                                onClick={() => handleStatusUpdate(store, 'sm',
                                  store.status.sm === 'not_started' ? 'in_progress' :
                                  store.status.sm === 'in_progress' ? 'done' : 'not_started'
                                )}
                                className="transition-transform hover:scale-105 duration-200"
                              >
                                <StatusBadge status={store.status.sm} />
                              </button>
                            </td>
                            <td className="p-6">
                              <button 
                                onClick={() => handleStatusUpdate(store, 'motion',
                                  store.status.motion === 'not_started' ? 'in_progress' :
                                  store.status.motion === 'in_progress' ? 'done' : 'not_started'
                                )}
                                className="transition-transform hover:scale-105 duration-200"
                              >
                                <StatusBadge status={store.status.motion} />
                              </button>
                            </td>
                            <td className="p-6">
                              <div className="flex flex-wrap gap-2">
                                {(store.team_members || []).map((member) => (
                                  <span
                                    key={member.id}
                                    className="px-3 py-1 bg-[rgb(44,44,46)]/60 backdrop-blur-md rounded-2xl text-sm text-white"
                                  >
                                    {member.name}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="p-6">
                              <div className="flex items-center gap-2">
                                <DeadlineIcon className={`w-5 h-5 ${deadlineStatus.color}`} />
                                <span className={`${deadlineStatus.color} text-sm`}>
                                  {new Date(store.deadline).toLocaleDateString('pt-BR', {
                                    day: '2-digit',
                                    month: 'short'
                                  }).replace('.', '')}
                                </span>
                              </div>
                            </td>
                            <td className="p-6 text-right">
                              <button
                                onClick={() => {
                                  setSelectedStore(store);
                                  setShowForm(true);
                                }}
                                className="p-2 hover:bg-[rgb(44,44,46)]/60 rounded-xl transition-all duration-200"
                              >
                                <ChevronRight className="w-5 h-5 text-[rgb(152,152,157)] hover:text-white transition-colors duration-200" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>

        {/* Modals */}
        {showForm && (
          <StoreForm
            store={selectedStore}
            onClose={() => {
              setShowForm(false);
              setSelectedStore(null);
            }}
            onSuccess={() => {
              loadStores();
            }}
          />
        )}

        {showTeamManagement && (
          <TeamManagement
            onClose={() => setShowTeamManagement(false)}
            onSuccess={() => {
              loadMembers();
              setShowTeamManagement(false);
            }}
          />
        )}
      </div>
    </div>
  );
};